import { Modal, Button } from '@rabbit/elements/shared-components';
import { useContext } from 'react';
import { AppContext } from '@rabbit/app-context';
import { DTVendable, UserUploadedDocument } from '@rabbit/data/types';
import { t } from 'i18next';
import placeholderImageSrc from '../../assets/images/picture_placeholder.png';

export interface SerialNumberHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  vendable: DTVendable;
}

export function SerialNumberHelpModal({ isOpen, onClose, vendable }: SerialNumberHelpModalProps) {
  const { config } = useContext(AppContext);

  const DEFAULT_HELP_TEXT =
    'Look for a label or plate on your product. Common locations include: the back or underside of the unit, near the power input, inside a battery compartment, on a rear/side sticker, or on the original packaging/receipt.';

  const serialNumberHelpTextDefault = t('CFG_SERIAL_NUMBER_HELP_TEXT');
  const serialNumberHelpTitle = config?.HOLDINGS?.REGISTRATION?.SERIAL_NUMBER_HELP?.TEXT;
  const serialNumberHelpText = vendable?.serialNumberHelp?.text ||
    serialNumberHelpTextDefault;
  const serialNumberHelpImage = vendable.serialNumberHelp?.image || [];
  const serialNumberHelpLink = vendable.serialNumberHelp?.link;
  const serialNumberHelpVideo = vendable.serialNumberHelp?.video;

  if (!isOpen) return null;

  const Section = ({
    icon,
    title,
    children,
  }: {
    icon?: React.ReactNode;
    title: string;
    children: React.ReactNode;
  }) => (
    <div className="rounded-xl bg-white p-4 shadow-sm border border-gray-100">
      <h3 className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
        {icon && <span className="text-lg">{icon}</span>}
        {title}
      </h3>
      {children}
    </div>
  );

  const renderMediaItem = (
    fileOrFiles: UserUploadedDocument | UserUploadedDocument[]
  ) => {
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    if (!files || files.length === 0) return null;
    return (
      <div className="py-1 space-y-2">
        <p className="text-base text-gray-800">Look at the image to find your serial number.</p>
        <div className="flex flex-col gap-2">
          {files.map((file, idx) => (
            <div key={file.metadata?.fullPath || file.url || idx} className="flex items-center">
              <Button
                kind="primary"
                size="sm"
                onClick={() => window.open(file.url, '_blank', 'noopener,noreferrer')}
              >
                Open
              </Button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const openExternal = (url: string) => {
    if (!url) return;
    const href = /^(https?:)?\/\//i.test(url) ? url : `https://${url}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };


  return (
    <Modal
      kind="generic"
      settings={{ title: 'Locate serial number', handleClose: onClose }}
      className="!w-auto w-[95vw] max-w-[460px] !h-auto lg:!h-fit lg:max-h-[90vh] lg:rounded-md"
    >
      <div className="p-5 space-y-4 bg-gray-50">
        {serialNumberHelpTitle && (
          <Section icon="📍" title="Where to find the serial number">
            <p className="text-gray-700 text-base leading-relaxed">{serialNumberHelpText}</p>
          </Section>
        )}

        {(vendable) && (
          <Section icon={<img src={placeholderImageSrc} className="h-4 w-4" alt="image placeholder" />} title="Media">
            {renderMediaItem(serialNumberHelpImage)}
          </Section>
        )}

        {( serialNumberHelpVideo) && (
          <Section icon={<img src={placeholderImageSrc} className="h-4 w-4" alt="image placeholder" />} title="Media">
            <div className="space-y-2">
              {serialNumberHelpVideo && renderMediaItem(serialNumberHelpVideo)}
            </div>
          </Section>
        )}

        {serialNumberHelpLink && (
          <Section icon="🔗" title="Help link">
            <div className="flex items-center justify-end">
              <Button kind="primary" size="sm" onClick={() => openExternal(serialNumberHelpLink)}>
                Open Link
              </Button>
            </div>
          </Section>
        )}
      </div>
    </Modal>
  );
}

export default SerialNumberHelpModal;


